import { ISO_3166_1_LOCALE } from '@cineamo/ui/src/helpers/locale-helper';

import { cineamoApiClient } from '../../apiClient';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { ContentDto, ContentEmbedsConstraint } from '../../models/content/ContentDto.types';
import { ContentInputDto } from '../../models/content/ContentInputDto';
import { MovieDto } from '../../models/movie/v2/MovieDto.types';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { ContentListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

// <------------------------ Contents ------------------------>

export type GetContentsQuery = {
    /** Filter by movie id. */
    movieId?: MovieDto['id'];
    showingStartDateTime?: string;
    showingEndDateTime?: string;
    metricStartDatetime?: string;
    metricEndDatetime?: string;
    cinemaId?: number | string;
    creatorCinemaId?: number | string;
    showingDate?: string;
    hasMarketingAssistantBooked?: boolean;
    isMovie?: boolean;
    isShowingNow?: boolean;
    cinemaCountryCode?: ISO_3166_1_LOCALE;
} & UrlQueryParameter;

/**
 * This endpoint returns a list of content.
 *
 * @example
 * // Get a list of content:
 * getContents({...query});
 *
 * @param query
 * @returns {ApiResponse<ContentListResponse>}
 */

export async function getContents<T extends ContentEmbedsConstraint = ContentEmbedsConstraint>(
    query: GetContentsQuery
): Promise<ApiResponse<ContentListResponse<T>> | null> {
    return cineamoApiClient
        .get<ApiResponse<ContentListResponse<T>>>(`/contents${buildUrlQuery(query)}`)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Content ------------------------>

/**
 * This endpoint returns a content.
 *
 * @example
 * // Get a single content:
 * getContentById(1);
 *
 * @param id
 * @returns {ApiResponse<ContentDto>}
 */

export async function getContentById(id: string | number): Promise<ApiResponse<ContentDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<ContentDto>>(`/contents/${id}`, {
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => null);
}

export async function updateContent(content: ContentInputDto): Promise<ApiResponse<ContentDto>> {
    return cineamoApiClient
        .patch<ApiResponse<ContentDto>>(`/contents/${content.id}`, content)
        .then((response) => response.data)
        .catch((error) => error);
}

export async function createContent(
    content: ContentInputDto
): Promise<ApiResponse<ContentDto> | ApiErrorResponse<ContentInputDto>> {
    return cineamoApiClient
        .post<ApiResponse<ContentDto>>('/contents', content)
        .then((response) => response.data)
        .catch((error) => error);
}
